import { auth } from '../config/firebase';
import { Subscription } from '../types';

const SHOP_API_BASE_URL = process.env.REACT_APP_SHOP_API_BASE_URL || 'https://shop.dev.carouselshopai.com';

// Response types for subscription endpoints
export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface BillingPortalResponse {
  url: string;
}

// Tier limits for pricing display
export interface TierLimits {
  maxSlides: number;
  maxActiveMembers: number;
  // CSAI-533. Optional so the UI still renders against a shop-svc that predates the field, and so
  // the comparison table can omit the row rather than showing "0" for an unconfigured tier.
  maxActiveClientMembers?: number;
  maxShopLocations: number;
  maxShopPlaces: number;
  maxMarketplaceEvalsPerMonth: number;
  maxQuickAnalyzesPerMonth: number;
  maxInvitationsPerDay?: number;
}

// Individual tier pricing
export interface TierPricing {
  tierType: string;        // PERSONAL, BUSINESS, BUSINESS_PLUS
  displayName: string;     // "Personal", "Business", "Business Plus"
  monthlyPriceUSD: number;
  stripePriceId: string;
  limits: TierLimits;
}

// Full pricing response
export interface SubscriptionPricingResponse {
  tiers: TierPricing[];
  trialDurationDays: number;
  trialLimits: TierLimits | null;  // Limits for PERSONAL_TRIAL tier
}

export interface SubscriptionStatusResponse {
  status: string | null;
  billingProvider: string | null;  // "stripe", "apple", or null
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  stripePriceId: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  trialEnd: string | null;
  trialStartedAt: string | null;
  firstPaidAt: string | null;
  isTrialing: boolean;
  isActive: boolean;
  isPastDue: boolean;
  isCanceled: boolean;
  isTrialExpired: boolean;
  allowsAccess: boolean;
  isLinkedToStripe: boolean;
  isEligibleForTrial: boolean;
}

class SubscriptionService {
  private async getAuthToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
    return null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = await this.getAuthToken();
    const url = `${SHOP_API_BASE_URL}${endpoint}`;

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
    }

    if (response.status === 204) {
      return {} as T; // No content
    }

    return response.json();
  }

  /**
   * Get subscription pricing information.
   * Returns all available tiers with pricing and limits.
   */
  async getPricing(): Promise<SubscriptionPricingResponse> {
    return this.request<SubscriptionPricingResponse>('/api/v1/subscription/pricing');
  }

  /**
   * Get current subscription status for a shop.
   * Requires OWNER or ADMIN role.
   */
  async getSubscriptionStatus(shopId: string): Promise<SubscriptionStatusResponse> {
    return this.request<SubscriptionStatusResponse>(`/api/v1/shops/${shopId}/subscription`);
  }

  /**
   * Create a Stripe Checkout session for subscription purchase.
   * Requires OWNER role. Use for new subscriptions only.
   * Shops with existing subscriptions should use the billing portal.
   */
  async createCheckoutSession(shopId: string, priceId: string): Promise<CheckoutSessionResponse> {
    return this.request<CheckoutSessionResponse>(`/api/v1/shops/${shopId}/subscription/checkout`, {
      method: 'POST',
      body: JSON.stringify({ priceId }),
    });
  }

  /**
   * Create a Stripe Billing Portal session for subscription management.
   * Requires OWNER role. Use for existing subscriptions to upgrade/downgrade/cancel.
   */
  async createBillingPortalSession(shopId: string): Promise<BillingPortalResponse> {
    return this.request<BillingPortalResponse>(`/api/v1/shops/${shopId}/subscription/portal`, {
      method: 'POST',
    });
  }

  /**
   * Upgrade or downgrade an existing subscription to a new price/tier.
   * Requires OWNER role. Stripe prorates charges automatically.
   */
  async upgradeSubscription(shopId: string, priceId: string): Promise<SubscriptionStatusResponse> {
    return this.request<SubscriptionStatusResponse>(`/api/v1/shops/${shopId}/subscription`, {
      method: 'PUT',
      body: JSON.stringify({ priceId }),
    });
  }

  /**
   * Cancel subscription at end of billing period.
   * Requires OWNER role.
   */
  async cancelSubscription(shopId: string, immediately: boolean = false): Promise<void> {
    await this.request<void>(`/api/v1/shops/${shopId}/subscription`, {
      method: 'DELETE',
      body: JSON.stringify({ cancelImmediately: immediately }),
    });
  }

  /**
   * Reactivate a subscription that was scheduled to cancel.
   * Requires OWNER role.
   */
  async reactivateSubscription(shopId: string): Promise<void> {
    await this.request<void>(`/api/v1/shops/${shopId}/subscription/reactivate`, {
      method: 'POST',
    });
  }

  /**
   * Activate a free trial for a PERSONAL_GUEST shop.
   * Requires OWNER role. Only available for shops that have never had a trial or paid subscription.
   */
  async activateTrial(shopId: string): Promise<SubscriptionStatusResponse> {
    return this.request<SubscriptionStatusResponse>(`/api/v1/shops/${shopId}/subscription/activate-trial`, {
      method: 'POST',
    });
  }

  // Utility methods for subscription status

  /**
   * Check if subscription allows access to the platform.
   */
  allowsAccess(subscription: Subscription | undefined): boolean {
    if (!subscription) return false;
    return ['trialing', 'active', 'past_due'].includes(subscription.status);
  }

  /**
   * Check if subscription is in trial period.
   */
  isTrialing(subscription: Subscription | undefined): boolean {
    return subscription?.status === 'trialing';
  }

  /**
   * Check if subscription is active (paid).
   */
  isActive(subscription: Subscription | undefined): boolean {
    return subscription?.status === 'active';
  }

  /**
   * Check if subscription is past due (payment failed).
   */
  isPastDue(subscription: Subscription | undefined): boolean {
    return subscription?.status === 'past_due';
  }

  /**
   * Check if subscription has been canceled.
   */
  isCanceled(subscription: Subscription | undefined): boolean {
    return subscription?.status === 'canceled';
  }

  /**
   * Get days remaining in trial period.
   */
  getTrialDaysRemaining(subscription: Subscription | undefined): number | null {
    if (!subscription?.trialEnd) return null;

    try {
      const trialEnd = new Date(subscription.trialEnd);
      const now = new Date();
      const diffMs = trialEnd.getTime() - now.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    } catch {
      return null;
    }
  }

  /**
   * Get human-readable subscription status label.
   */
  getStatusLabel(subscription: Subscription | undefined): string {
    if (!subscription) return 'No Subscription';

    switch (subscription.status) {
      case 'trialing':
        const daysLeft = this.getTrialDaysRemaining(subscription);
        return daysLeft !== null ? `Trial (${daysLeft} days left)` : 'Trial';
      case 'active':
        return subscription.cancelAtPeriodEnd ? 'Active (Canceling)' : 'Active';
      case 'past_due':
        return 'Past Due';
      case 'canceled':
        return 'Canceled';
      case 'incomplete':
        return 'Incomplete';
      case 'trial_expired':
        return 'Trial Expired';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get status color for UI display.
   */
  getStatusColor(subscription: Subscription | undefined): 'green' | 'yellow' | 'red' | 'gray' {
    if (!subscription) return 'gray';

    switch (subscription.status) {
      case 'active':
        return subscription.cancelAtPeriodEnd ? 'yellow' : 'green';
      case 'trialing':
        return 'green';
      case 'past_due':
        return 'yellow';
      case 'canceled':
      case 'trial_expired':
      case 'incomplete':
        return 'red';
      default:
        return 'gray';
    }
  }
}

export const subscriptionService = new SubscriptionService();
